// V17.9.8 — Biometric login (FaceID / TouchID / Fingerprint) para o app nativo.
// Guarda o JWT no Keychain (iOS) / Keystore (Android) protegido por biometria.
// Web preview: no-op (funções retornam false / null sem crashar).

import { Capacitor } from "@capacitor/core";

const SERVER = "app.br.alugaja.auth";
const USERNAME = "session";
const PREF_KEY = "aj_biometric_opt_in";

const isNative = () => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

// Import dinâmico para o bundle web não carregar código nativo sem necessidade.
async function getPlugin() {
  if (!isNative()) return null;
  try {
    const mod = await import("@capgo/capacitor-native-biometric");
    return mod.NativeBiometric || (mod.default && mod.default.NativeBiometric) || null;
  } catch (e) {
    console.warn("[biometric] plugin unavailable:", e?.message || e);
    return null;
  }
}

export async function biometricAvailable() {
  const NB = await getPlugin();
  if (!NB) return { available: false, reason: "not_native" };
  try {
    const r = await NB.isAvailable({ useFallback: true });
    return { available: !!r?.isAvailable, biometryType: r?.biometryType };
  } catch (e) {
    return { available: false, reason: e?.message || "unavailable" };
  }
}

// Chama o prompt do FaceID/TouchID sem retornar segredo (usado quando o plugin
// não faz gate no getCredentials — no plugin do capgo, o getSecureCredentials
// já dispara o prompt automaticamente).
export async function verifyBiometric(reason = "Entrar no AlugaJá com biometria") {
  const NB = await getPlugin();
  if (!NB) return false;
  try {
    await NB.verifyIdentity({
      reason,
      title: "Login com biometria",
      subtitle: "Toque no sensor ou olhe para a câmera",
      description: "Use Face ID, Touch ID ou digital para desbloquear sua conta",
      negativeButtonText: "Usar senha",
    });
    return true;
  } catch (e) {
    console.warn("[biometric] verify canceled/failed:", e?.message);
    return false;
  }
}

// Salva o JWT protegido por biometria. Chamado logo após um login bem-sucedido
// quando o usuário optou pelo "Entrar com biometria da próxima vez".
export async function saveBiometricToken(token) {
  const NB = await getPlugin();
  if (!NB || !token) return false;
  try {
    await NB.setCredentials({
      username: USERNAME,
      password: String(token),
      server: SERVER,
    });
    try { localStorage.setItem(PREF_KEY, "1"); } catch { /* ignore */ }
    return true;
  } catch (e) {
    console.warn("[biometric] setCredentials failed:", e?.message);
    return false;
  }
}

// Recupera o JWT — dispara o prompt biométrico do sistema. Retorna null se
// o usuário cancelar ou se não houver credencial salva.
export async function getBiometricToken() {
  const NB = await getPlugin();
  if (!NB) return null;
  try {
    // getCredentials retorna qualquer credencial (sem gate); verifyIdentity faz o gate biométrico.
    // Fazemos os dois para garantir prompt ANTES de expor o token.
    await NB.verifyIdentity({
      reason: "Desbloquear sua conta no AlugaJá",
      title: "Login com biometria",
      subtitle: "Confirme sua identidade",
      description: "Toque no sensor ou olhe para a câmera",
      negativeButtonText: "Usar senha",
    });
    const r = await NB.getCredentials({ server: SERVER });
    return r?.password || null;
  } catch (e) {
    console.warn("[biometric] getCredentials failed:", e?.message);
    return null;
  }
}

export async function clearBiometricToken() {
  const NB = await getPlugin();
  try { localStorage.removeItem(PREF_KEY); } catch { /* ignore */ }
  if (!NB) return true;
  try {
    await NB.deleteCredentials({ server: SERVER });
    return true;
  } catch (e) {
    console.warn("[biometric] deleteCredentials failed:", e?.message);
    return false;
  }
}

// True quando o usuário já ativou biometria neste device (opt-in salvo).
export function isBiometricOptedIn() {
  try { return localStorage.getItem(PREF_KEY) === "1"; } catch { return false; }
}

export function setBiometricOptIn(v) {
  try { v ? localStorage.setItem(PREF_KEY, "1") : localStorage.removeItem(PREF_KEY); } catch { /* ignore */ }
}
